import { useCallback, useEffect, useMemo, useRef } from 'react'
import debounce from 'lodash/debounce'
import { Popover } from 'gu-ui-kit'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

export const DRAWER_FOOTER_POPOVER_MIN_WIDTH = 768

export const DRAWER_FOOTER_POPOVER_CLOSE_DELAY_MS = 150

export const DRAWER_FOOTER_POPOVER_BREAKPOINTS = {
  0: {
    variant: 'bottomSheet',
    hasBackdrop: true,
  },
  [DRAWER_FOOTER_POPOVER_MIN_WIDTH]: {
    variant: 'popover',
    hasBackdrop: false,
  },
}

/** bottomSheet (<768) → click; popover (≥768) → hover */
export const shouldUseDrawerFooterHoverMode = (isPopoverViewport) => isPopoverViewport

export const getDrawerFooterInteractionConfig = ({ isPopoverViewport, collapse }) => {
  const useHoverMode = shouldUseDrawerFooterHoverMode(isPopoverViewport)

  return {
    useHoverMode,
    popoverEvents: useHoverMode ? DRAWER_FOOTER_HOVER_POPOVER_EVENTS : {},
    popoverConfig: useHoverMode ? getDrawerFooterPopoverConfig(collapse) : {},
  }
}

export const DRAWER_FOOTER_EXPANDED_POPOVER_CONFIG = {
  anchorOrigin: { vertical: 'top', horizontal: 'left' },
  transformOrigin: { vertical: 'bottom', horizontal: 'left' },
  stretch: true,
}

export const DRAWER_FOOTER_HOVER_POPOVER_EVENTS = {
  triggerOpenEventName: 'onMouseEnter',
  triggerCloseEventName: 'onMouseLeave',
}

export const getDrawerFooterPopoverConfig = (collapse) => {
  if (collapse) {
    return {
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      transformOrigin: { vertical: 'top', horizontal: 8 },
      stretch: false,
    }
  }

  return DRAWER_FOOTER_EXPANDED_POPOVER_CONFIG
}

export const getDrawerFooterAnchorEl = (
  footerAnchorRef,
  triggerEl,
  collapse,
  { useFooterAnchor = true } = {},
) => {
  if (collapse || !useFooterAnchor) {
    return triggerEl
  }

  return (
    footerAnchorRef?.current ??
    document.querySelector('[data-drawer-footer-anchor]') ??
    triggerEl
  )
}

export const bindDrawerFooterTrigger = (
  bindTrigger,
  popoverState,
  { collapse, footerAnchorRef, useFooterAnchor = true, onScheduleClose, onCancelScheduledClose },
) => {
  const { onMouseEnter: _onMouseEnter, onMouseLeave: _onMouseLeave, ...rest } =
    bindTrigger(popoverState)

  return {
    ...rest,
    onMouseEnter: (event) => {
      event.stopPropagation()
      if (onCancelScheduledClose) {
        onCancelScheduledClose()
      }

      const anchorEl = getDrawerFooterAnchorEl(footerAnchorRef, event.currentTarget, collapse, {
        useFooterAnchor,
      })

      if (!anchorEl) {
        return
      }

      popoverState.open({
        id: popoverState.id,
        anchorEl,
      })
    },
    onMouseLeave: () => {
      if (onScheduleClose) {
        onScheduleClose()
      }
    },
  }
}

export const useDrawerFooterMenuInteractions = ({ collapse, footerAnchorRef, id }) => {
  const breakpoints = useBreakpoint()
  const { useHoverMode, popoverEvents, popoverConfig } = useMemo(
    () =>
      getDrawerFooterInteractionConfig({
        isPopoverViewport: breakpoints.fb,
        collapse,
      }),
    [breakpoints.fb, collapse],
  )
  const { bindTrigger, bindPopover, usePopupState } = Popover.usePopover(popoverEvents)
  const popoverState = usePopupState({
    variant: 'popover',
    id,
  })

  const closeRef = useRef(popoverState.close)
  closeRef.current = popoverState.close

  const scheduleCloseRef = useRef(null)

  useEffect(() => {
    scheduleCloseRef.current = debounce(() => {
      closeRef.current()
    }, DRAWER_FOOTER_POPOVER_CLOSE_DELAY_MS)

    return () => {
      if (scheduleCloseRef.current) {
        scheduleCloseRef.current.cancel()
      }
    }
  }, [])

  const cancelScheduledClose = useCallback(() => {
    if (scheduleCloseRef.current) {
      scheduleCloseRef.current.cancel()
    }
  }, [])

  const scheduleClose = useCallback(() => {
    if (scheduleCloseRef.current) {
      scheduleCloseRef.current()
    }
  }, [])

  const handleContentMouseEnter = useCallback(() => {
    popoverState.lock()
    cancelScheduledClose()
  }, [cancelScheduledClose, popoverState])

  const handleContentMouseLeave = useCallback(() => {
    popoverState.lock()
    scheduleClose()
  }, [popoverState, scheduleClose])

  const triggerBindings = useMemo(
    () =>
      useHoverMode
        ? bindDrawerFooterTrigger(bindTrigger, popoverState, {
            collapse,
            footerAnchorRef,
            onScheduleClose: scheduleClose,
            onCancelScheduledClose: cancelScheduledClose,
          })
        : bindTrigger(popoverState),
    [bindTrigger, cancelScheduledClose, collapse, footerAnchorRef, popoverState, scheduleClose, useHoverMode],
  )

  const popoverAnchorEl = useMemo(() => {
    if (!useHoverMode || collapse) {
      return popoverState.anchorEl
    }

    return (
      footerAnchorRef?.current ??
      document.querySelector('[data-drawer-footer-anchor]') ??
      popoverState.anchorEl
    )
  }, [collapse, footerAnchorRef, popoverState.anchorEl, popoverState.isOpen, useHoverMode])

  const popoverBindings = useMemo(
    () => ({
      ...bindPopover(popoverState),
      ...(useHoverMode ? { anchorEl: popoverAnchorEl } : {}),
      ...popoverConfig,
      breakpoints: DRAWER_FOOTER_POPOVER_BREAKPOINTS,
    }),
    [bindPopover, popoverAnchorEl, popoverConfig, popoverState, useHoverMode],
  )

  const contentHoverHandlers = useMemo(
    () =>
      useHoverMode
        ? {
            onMouseEnter: handleContentMouseEnter,
            onMouseLeave: handleContentMouseLeave,
          }
        : {},
    [handleContentMouseEnter, handleContentMouseLeave, useHoverMode],
  )

  return {
    useHoverMode,
    popoverState,
    triggerBindings,
    popoverBindings,
    contentHoverHandlers,
  }
}
